// Component: ConfirmDialog.jsx
// Purpose: Reusable confirm/cancel modal for live-money safety steps (operator cancel R5.2.1,
//          market-conversion modify R6.6). No ds Modal/Dialog primitive exists today (grepped
//          frontend/src/components/ds — Button/Card/Badge/DataTable/GlassPanel/PageHeader only);
//          this mirrors OrderDetailDrawer's backdrop+Escape-to-close pattern
//          (frontend/src/components/trading/OrderDetailDrawer.jsx:200-213) rather than inventing
//          a new modal mechanism.

import React, { useEffect, useCallback } from 'react';
import { Button } from '../ds';

/*>>> MAIN COMPONENT <<<*/

/**
 * ConfirmDialog — blocking confirm/cancel step. Renders nothing when `open` is false, so it
 * fires ZERO network requests until the operator explicitly confirms (R5.2.1/R6.6 AC).
 *
 * @param {boolean}  open
 * @param {string}   title
 * @param {React.ReactNode} message   - body content (string or JSX)
 * @param {string}   confirmLabel
 * @param {string}   cancelLabel
 * @param {boolean}  danger           - true renders the confirm action as the danger variant
 * @param {Function} onConfirm        - () => void, fired only on explicit confirm
 * @param {Function} onCancel         - () => void, fired on dismiss (backdrop click / Escape / Cancel button)
 */
const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  onConfirm,
  onCancel,
}) => {
  /*>>> EFFECTS <<<*/

  //! Escape dismisses (same as OrderDetailDrawer) — dismiss fires zero network requests
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape' && open) {
        onCancel?.();
      }
    },
    [open, onCancel]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  /*>>> RENDER <<<*/
  if (!open) return null;

  return (
    <>
      {/* Backdrop — click dismisses (no network request) */}
      <div
        className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm"
        aria-hidden="true"
        onClick={() => onCancel?.()}
      />

      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={title}
        className="fixed left-1/2 top-1/2 z-[70] w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-xl border border-divider/50 bg-bg-2 p-5 shadow-2xl"
      >
        <h3 className="text-sm font-semibold text-fg-1">{title}</h3>
        {message != null && (
          <div className="mt-2 text-sm text-fg-2">{message}</div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => onCancel?.()}>
            {cancelLabel}
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            size="sm"
            onClick={() => onConfirm?.()}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </>
  );
};

export default ConfirmDialog;
